/**
 * Downward Routing
 *
 * Most common case: target is below source
 * Connection: bottom of source → top of target
 *
 * Builds orthogonal paths and validates every segment against obstacles.
 * Uses expanded search + five-segment "corridor" fallback so we avoid returning
 * paths that cut through nodes when any clear route exists in the search space.
 */

import { CORNER_RADIUS, CLEARANCE, MIN_PERPENDICULAR_DISTANCE, NODE_WIDTH, ROUTING_OBSTACLE_INFLATION } from '../constants';
import { pathBlockedByNodes, findClearY } from '../pathHelpers';
import { buildPathWithCorners } from '../pathBuilders';

// Minimum raw segment length at exit/entry so that after corner rounding
// there is still MIN_PERPENDICULAR_DISTANCE of visible straight line.
const EXIT_CLEARANCE = MIN_PERPENDICULAR_DISTANCE + CORNER_RADIUS;

/** How far we search sideways for a clear vertical column */
const CLEAR_X_MAX_OFFSET = 5000;
const CLEAR_X_STEP = 12;

/** midY scan: target at most this many samples along the span (step grows for tall graphs) */
const MID_Y_SCAN_MAX_SAMPLES = 200;

/** Corridor fallback: X sweep step when discrete candidates fail */
const CORRIDOR_X_SWEEP_STEP = 20;

const MARGIN_OUTSIDE = 400;

/**
 * Route downward (normal case: target below source)
 * @param {Object} offsets - Optional { startXOffset, endXOffset, midYOffset } for edge separation
 * @param {string} edgeId - Optional edge ID for hysteresis tracking
 */
export function routeDownward(startX, startY, endX, endY, obstacles, edgeIndex, offsets = null, edgeId = null) {
  const startXOffset = offsets?.startXOffset || 0;
  const endXOffset = offsets?.endXOffset || 0;
  const midYOffset = offsets?.midYOffset || 0;

  const sx = startX + startXOffset;
  const ex = endX + endXOffset;

  // Straight vertical - no corners needed
  if (Math.abs(sx - ex) < 3 && !pathBlockedByNodes(sx, startY, sx, endY, obstacles)) {
    return buildPathWithCorners([{ x: sx, y: startY }, { x: sx, y: endY }], CORNER_RADIUS);
  }

  // Standard down-across-down: find a clear midY for the horizontal segment
  let midY = startY + (endY - startY) / 2 + midYOffset;
  const hMinX = Math.min(sx, ex);
  const hMaxX = Math.max(sx, ex);

  if (!offsets?.forcedMidY) {
    midY = findClearY(midY, startY + EXIT_CLEARANCE, endY - EXIT_CLEARANCE, hMinX, hMaxX, obstacles, edgeId ? `${edgeId}-h` : null);
  } else {
    midY = Math.max(startY + EXIT_CLEARANCE, Math.min(midY, endY - EXIT_CLEARANCE));
  }

  // Try the simple 3-segment path first (down, across, down)
  const simplePath = tryPath(sx, startY, ex, endY, sx, ex, midY, obstacles);
  if (simplePath) return simplePath;

  // Vertical segments are blocked - find clear X positions
  const leftResult = findClearRoute(sx, startY, ex, endY, midY, obstacles, -1);
  const rightResult = findClearRoute(sx, startY, ex, endY, midY, obstacles, 1);

  const best = pickBestRoute(leftResult, rightResult, sx, ex);
  if (best) return best;

  // Scan midY values with shifted X positions (step scales on very tall edges)
  const span = endY - startY;
  const midYStep = Math.max(8, Math.ceil(span / MID_Y_SCAN_MAX_SAMPLES));
  for (let yOff = 0; yOff <= span; yOff += midYStep) {
    for (const sign of [1, -1]) {
      const testMidY = midY + yOff * sign;
      if (testMidY <= startY + EXIT_CLEARANCE || testMidY >= endY - EXIT_CLEARANCE) continue;

      const lr = findClearRoute(sx, startY, ex, endY, testMidY, obstacles, -1);
      const rr = findClearRoute(sx, startY, ex, endY, testMidY, obstacles, 1);
      const b = pickBestRoute(lr, rr, sx, ex);
      if (b) return b;
    }
  }

  // Five-segment corridor: vertical trunk at X = V away from the dense column
  const corridor = tryFiveSegmentCorridor(sx, startY, ex, endY, obstacles);
  if (corridor) return corridor;

  // Last resort: same geometry as legacy fallback (may still intersect in pathological cases)
  return buildPathWithCorners(
    [
      { x: sx, y: startY },
      { x: sx, y: midY },
      { x: ex, y: midY },
      { x: ex, y: endY },
    ],
    CORNER_RADIUS
  );
}

/**
 * Try to build a path with given via-X positions and midY.
 * Returns the path string if ALL segments are clear, null otherwise.
 */
function tryPath(sx, sy, ex, ey, viaStartX, viaEndX, midY, obstacles) {
  const points = buildPoints(sx, sy, ex, ey, viaStartX, viaEndX, midY);
  if (allSegmentsClear(points, obstacles)) {
    return buildPathWithCorners(points, CORNER_RADIUS);
  }
  return null;
}

/**
 * Build the point sequence for a down-across-down path,
 * including shift segments if viaX differs from start/end X.
 */
function buildPoints(sx, sy, ex, ey, viaStartX, viaEndX, midY) {
  const points = [];
  points.push({ x: sx, y: sy });

  if (Math.abs(viaStartX - sx) > 1) {
    const shiftY = sy + EXIT_CLEARANCE;
    points.push({ x: sx, y: shiftY });
    points.push({ x: viaStartX, y: shiftY });
  }

  points.push({ x: viaStartX, y: midY });
  points.push({ x: viaEndX, y: midY });

  if (Math.abs(viaEndX - ex) > 1) {
    const shiftY = ey - EXIT_CLEARANCE;
    points.push({ x: viaEndX, y: shiftY });
    points.push({ x: ex, y: shiftY });
  }

  points.push({ x: ex, y: ey });

  return points.filter((p, i) => {
    if (i === 0) return true;
    const prev = points[i - 1];
    return Math.abs(p.x - prev.x) > 0.5 || Math.abs(p.y - prev.y) > 0.5;
  });
}

/**
 * Check that every segment in a point sequence is clear of obstacles.
 */
function allSegmentsClear(points, obstacles) {
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (pathBlockedByNodes(a.x, a.y, b.x, b.y, obstacles)) {
      return false;
    }
  }
  return true;
}

/**
 * Find a clear route by shifting blocked vertical segments in one direction.
 * Also validates the horizontal shift segments.
 */
function findClearRoute(sx, sy, ex, ey, midY, obstacles, direction) {
  let viaStartX = sx;
  let viaEndX = ex;

  if (pathBlockedByNodes(sx, sy, sx, midY, obstacles)) {
    viaStartX = findClearX(sx, sy, midY, obstacles, direction);
    if (viaStartX === null) return null;
  }

  if (pathBlockedByNodes(ex, midY, ex, ey, obstacles)) {
    viaEndX = findClearX(ex, midY, ey, obstacles, direction);
    if (viaEndX === null) return null;
  }

  const points = buildPoints(sx, sy, ex, ey, viaStartX, viaEndX, midY);
  if (allSegmentsClear(points, obstacles)) {
    return {
      path: buildPathWithCorners(points, CORNER_RADIUS),
      totalShift: Math.abs(viaStartX - sx) + Math.abs(viaEndX - ex),
    };
  }

  const shiftStartY = findClearShiftY(sx, viaStartX, sy + EXIT_CLEARANCE, sy, midY, obstacles);
  const shiftEndY = findClearShiftY(viaEndX, ex, ey - EXIT_CLEARANCE, midY, ey, obstacles);

  if (shiftStartY !== null && shiftEndY !== null) {
    const points2 = [];
    points2.push({ x: sx, y: sy });
    if (Math.abs(viaStartX - sx) > 1) {
      points2.push({ x: sx, y: shiftStartY });
      points2.push({ x: viaStartX, y: shiftStartY });
    }
    points2.push({ x: viaStartX, y: midY });
    points2.push({ x: viaEndX, y: midY });
    if (Math.abs(viaEndX - ex) > 1) {
      points2.push({ x: viaEndX, y: shiftEndY });
      points2.push({ x: ex, y: shiftEndY });
    }
    points2.push({ x: ex, y: ey });

    const filtered = points2.filter((p, i) => {
      if (i === 0) return true;
      const prev = points2[i - 1];
      return Math.abs(p.x - prev.x) > 0.5 || Math.abs(p.y - prev.y) > 0.5;
    });

    if (allSegmentsClear(filtered, obstacles)) {
      return {
        path: buildPathWithCorners(filtered, CORNER_RADIUS),
        totalShift: Math.abs(viaStartX - sx) + Math.abs(viaEndX - ex),
      };
    }
  }

  return null;
}

/**
 * Find a clear X for a vertical segment, searching in one direction.
 */
function findClearX(preferredX, y1, y2, obstacles, direction) {
  for (let offset = 0; offset < CLEAR_X_MAX_OFFSET; offset += CLEAR_X_STEP) {
    const testX = preferredX + offset * direction;
    if (!pathBlockedByNodes(testX, y1, testX, y2, obstacles)) {
      return testX;
    }
  }
  return null;
}

/**
 * Find a clear Y for a horizontal shift segment between x1 and x2.
 * Searches from preferred Y outward within [minY, maxY].
 */
function findClearShiftY(x1, x2, preferredY, minY, maxY, obstacles) {
  const lo = Math.min(x1, x2);
  const hi = Math.max(x1, x2);
  if (Math.abs(x1 - x2) < 1) return preferredY;

  if (!pathBlockedByNodes(lo, preferredY, hi, preferredY, obstacles)) {
    return preferredY;
  }

  for (let offset = 10; offset < 2000; offset += 10) {
    const yDown = preferredY + offset;
    if (yDown < maxY - CLEARANCE && !pathBlockedByNodes(lo, yDown, hi, yDown, obstacles)) {
      return yDown;
    }
    const yUp = preferredY - offset;
    if (yUp > minY + CLEARANCE && !pathBlockedByNodes(lo, yUp, hi, yUp, obstacles)) {
      return yUp;
    }
  }
  return null;
}

/**
 * Pick the best route from left and right attempts.
 */
function pickBestRoute(leftResult, rightResult, sx, ex) {
  if (!leftResult && !rightResult) return null;
  if (!leftResult) return rightResult.path;
  if (!rightResult) return leftResult.path;
  if (leftResult.totalShift <= rightResult.totalShift) return leftResult.path;
  return rightResult.path;
}

/**
 * Obstacle span in X for building sweep bounds.
 */
function obstacleXExtents(obstacles) {
  let minX = Infinity;
  let maxX = -Infinity;
  for (const node of obstacles) {
    if (!node.pos || !node.dims) continue;
    const L = node.pos.x - ROUTING_OBSTACLE_INFLATION;
    const R = node.pos.x + (node.dims.width || NODE_WIDTH) + ROUTING_OBSTACLE_INFLATION;
    minX = Math.min(minX, L);
    maxX = Math.max(maxX, R);
  }
  return { minX, maxX };
}

/**
 * Five-segment path: down from source → across to trunk X → vertical trunk →
 * across to target X → down to target. Finds any trunk X with all segments clear.
 */
function tryFiveSegmentCorridor(sx, sy, ex, ey, obstacles) {
  const innerLow = sy + EXIT_CLEARANCE;
  const innerHigh = ey - EXIT_CLEARANCE;
  if (innerHigh <= innerLow + 6) {
    return null;
  }
  const innerSpan = innerHigh - innerLow;
  const inset = Math.min(innerSpan * 0.22, 140);
  const yLeg1 = innerLow + inset;
  const yLeg2 = innerHigh - inset;
  if (yLeg2 <= yLeg1 + 4) {
    return null;
  }

  const candidates = new Set();
  candidates.add(sx);
  candidates.add(ex);
  candidates.add((sx + ex) / 2);

  for (const node of obstacles) {
    if (!node.pos || !node.dims) continue;
    const w = node.dims.width || NODE_WIDTH;
    const L = node.pos.x - ROUTING_OBSTACLE_INFLATION - 28;
    const R = node.pos.x + w + ROUTING_OBSTACLE_INFLATION + 28;
    candidates.add(L);
    candidates.add(R);
  }

  const { minX: oxMin, maxX: oxMax } = obstacleXExtents(obstacles);
  const sweepMin = (Number.isFinite(oxMin) ? Math.min(oxMin, sx, ex) : Math.min(sx, ex)) - MARGIN_OUTSIDE;
  const sweepMax = (Number.isFinite(oxMax) ? Math.max(oxMax, sx, ex) : Math.max(sx, ex)) + MARGIN_OUTSIDE;

  const sweepSpan = sweepMax - sweepMin;
  let sweepStep = CORRIDOR_X_SWEEP_STEP;
  if (sweepSpan / sweepStep > 400) {
    sweepStep = Math.ceil(sweepSpan / 400);
  }
  for (let vx = sweepMin; vx <= sweepMax; vx += sweepStep) {
    candidates.add(vx);
  }

  const sorted = [...candidates].filter(Number.isFinite).sort((a, b) => {
    const da = Math.abs(a - (sx + ex) / 2);
    const db = Math.abs(b - (sx + ex) / 2);
    return da - db;
  });

  let bestPath = null;
  let bestCost = Infinity;

  for (const V of sorted) {
    const pts = [
      { x: sx, y: sy },
      { x: sx, y: yLeg1 },
      { x: V, y: yLeg1 },
      { x: V, y: yLeg2 },
      { x: ex, y: yLeg2 },
      { x: ex, y: ey },
    ];
    const filtered = pts.filter((p, i) => {
      if (i === 0) return true;
      const prev = pts[i - 1];
      return Math.abs(p.x - prev.x) > 0.5 || Math.abs(p.y - prev.y) > 0.5;
    });
    if (!allSegmentsClear(filtered, obstacles)) continue;

    const cost =
      Math.abs(V - sx) +
      Math.abs(V - ex) +
      Math.abs(yLeg2 - yLeg1) +
      Math.abs(yLeg1 - sy) +
      Math.abs(ey - yLeg2);
    if (cost < bestCost) {
      bestCost = cost;
      bestPath = buildPathWithCorners(filtered, CORNER_RADIUS);
    }
  }

  return bestPath;
}
